import { AccountInfo } from "@azure/msal-browser";
import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { UserInfoResponse } from "auth/models/UserInfoResponse";
import { UserTenantInfo } from "auth/models/UserTenantInfo";
import httpClient from "common/api/httpClient";
import isErrorAction from "common/api/utils/isErrorAction";
import isForbiddenErrorAction from "common/api/utils/isForbiddenErrorAction";
import isUnauthenticatedErrorAction from "common/api/utils/isUnauthenticatedErrorAction";

type InitialState = {
  isLoading: boolean;
  error?: string;
  isReloadNeeded: boolean;
  tenants: UserTenantInfo[];
  account: AccountInfo | undefined;
  isUnauthorized: boolean;
  isForbidden: boolean;
};

const initialState: InitialState = {
  isLoading: false,
  isReloadNeeded: true,
  account: undefined,
  isUnauthorized: false,
  isForbidden: false,
  tenants: [],
};

export const getUserInfo = createAsyncThunk("User/GetUserInfo", async () => {
  const { data } = await httpClient.get<UserInfoResponse>("/UserInfo");

  return data.tenants;
});

const authenticationSlice = createSlice({
  name: "Auth",
  initialState,
  reducers: {
    setIsLoggedIn: (state, action: PayloadAction<AccountInfo | undefined>) => {
      state.account = action.payload;
      state.isUnauthorized = false;
      state.isForbidden = false;
    },
    setIsUnauthorized: (state) => {
      state.isUnauthorized = true;
    },
    closeForbiddenError: (state) => {
      state.isForbidden = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUserInfo.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getUserInfo.fulfilled, (state, action) => {
        state.tenants = action.payload ?? [];
        state.isReloadNeeded = false;
        state.isLoading = false;
      })
      .addCase(getUserInfo.rejected, (state, action) => {
        state.error = action.error.message;
        state.isLoading = false;
        state.isReloadNeeded = false;
        state.tenants = [];
      })
      .addMatcher(
        (action) =>
          isErrorAction(action) && isUnauthenticatedErrorAction(action),
        (state) => {
          state.isUnauthorized = true;
        }
      );

    builder.addMatcher(
      (action) => isErrorAction(action) && isForbiddenErrorAction(action),
      (state) => {
        state.isForbidden = true;
      }
    );
  },
});

export const { setIsLoggedIn, closeForbiddenError } =
  authenticationSlice.actions;

export default authenticationSlice.reducer;
